import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute, PageLoader } from "@/components/ProtectedRoute";
import PublicLayout from "@/layouts/PublicLayout";
import Home from "@/pages/Home";
import FilePage from "@/pages/FilePage";
import AuthCallback from "@/pages/AuthCallback";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/AuthPages";
import { Blog, BlogPost, LegalPage, NotFound } from "@/pages/ContentPages";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Settings = lazy(() => import("@/pages/Settings"));
const AdminLayout = lazy(() => import("@/layouts/AdminLayout"));
const AdminDashboard = lazy(() => import("@/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("@/admin/AdminUsers"));
const AdminFiles = lazy(() => import("@/admin/AdminFiles"));
const AdminReports = lazy(() => import("@/admin/AdminReports"));
const AdminSite = lazy(() => import("@/admin/AdminSite"));
const AdminBlogPages = lazy(() => import("@/admin/AdminBlog").then((m) => ({ default: m.AdminBlog })));
const AdminBlogEditor = lazy(() => import("@/admin/AdminBlog").then((m) => ({ default: m.AdminBlogEditor })));
const AdminStorage = lazy(() => import("@/admin/AdminSystemPages").then((m) => ({ default: m.AdminStorage })));
const AdminSystem = lazy(() => import("@/admin/AdminSystemPages").then((m) => ({ default: m.AdminSystem })));
const AdminLogs = lazy(() => import("@/admin/AdminSystemPages").then((m) => ({ default: m.AdminLogs })));

const ADMIN_ROLES = ["admin", "super_admin"];

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/f/:publicId" element={<FilePage />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/terms" element={<LegalPage slug="terms" />} />
          <Route path="/privacy" element={<LegalPage slug="privacy" />} />
          <Route path="/cookies" element={<LegalPage slug="cookies" />} />
          <Route path="/abuse" element={<LegalPage slug="abuse" />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/admin" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminLayout /></ProtectedRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="files" element={<AdminFiles />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="blog" element={<AdminBlogPages />} />
          <Route path="blog/:postId" element={<AdminBlogEditor />} />
          <Route path="site" element={<AdminSite />} />
          <Route path="storage" element={<AdminStorage />} />
          <Route path="system" element={<AdminSystem />} />
          <Route path="logs" element={<AdminLogs />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="bottom-center" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
